import { types as api } from '@mesa-labs/mesa-api';
import { Button } from '@mesa-labs/mesa-ui';
import React from 'react';
import styled from 'styled-components';

import { VendorOption } from '../../../../utils/vendorActionItems';
import VendorSelect from '../../../common/VendorSelect';
import { useActionItemDraft } from '../hooks';
import VendorActionItemsSection from './VendorActionItemsSection';
import { AssigneeOptions } from './SelectOptions';
import SourceNoteQuote from './SourceNoteQuote';
import { ErrorText } from './styles';

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Label = styled.label`
  color: ${(p) => p.theme.colors.Gray700};
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 600;
  gap: 4px;
`;

const TextInput = styled.input`
  border: 1px solid ${(p) => p.theme.colors.Gray300};
  border-radius: 6px;
  font-size: 14px;
  padding: 8px 10px;

  &:focus { border-color: #9E77ED; outline: none; }
`;

const TextArea = styled.textarea`
  border: 1px solid ${(p) => p.theme.colors.Gray300};
  border-radius: 6px;
  font-family: inherit;
  font-size: 13px;
  min-height: 110px;
  padding: 8px 10px;
  resize: vertical;

  &:focus { border-color: #9E77ED; outline: none; }
`;

const Select = styled.select`
  border: 1px solid ${(p) => p.theme.colors.Gray300};
  border-radius: 6px;
  font-size: 13px;
  padding: 8px;
`;

// react-select renders divs, which would otherwise inherit the label's bold.
const PickerControl = styled.div`
  font-weight: 400;
`;

const FixedValue = styled.div`
  color: #101828;
  font-size: 14px;
  font-weight: 600;
  padding: 4px 0;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

// Pick the Vendor in the form (the cross-Vendor board) instead of creating for a fixed one.
export type VendorPicker = {
  readonly vendorId?: string;
  readonly onChange: (vendorId?: string) => void;
  // Offered before typing, e.g. the Vendors on the board.
  readonly quickOptions?: readonly VendorOption[];
  // Opens one of the picked Vendor's open items, listed so a duplicate isn't created.
  readonly onOpenItem: (item: api.VendorActionItemResponse) => void;
};

type VendorField =
  | { vendorName: string; vendorPicker?: undefined }
  | { vendorName?: undefined; vendorPicker: VendorPicker };

type Props = VendorField & {
  me?: string;
  // Prefills the assignee (e.g. an assignee lane's); defaults to me.
  initialAssignee?: string;
  operators: readonly api.VendorActionItemAssigneeResponse[];
  // Create-from-Note: previews the Note and records it as the item's source; nothing is prefilled.
  sourceNote?: api.VendorNoteResponse;
  error?: string;
  onCreate: (request: api.CreateVendorActionItemRequest) => Promise<unknown>;
  onCancel: () => void;
};

// Title for the drawer around the form.
export const createActionItemTitle = (sourceNote?: api.VendorNoteResponse): string => (
  sourceNote ? 'New Action Item from Note' : 'New Action Item'
);

export default function CreateActionItemForm({
  vendorName, vendorPicker, me, initialAssignee = me, operators, sourceNote, error, onCreate, onCancel,
}: Props): React.ReactElement {
  const draft = useActionItemDraft({ initialAssignee, sourceNote, onCreate });
  const canCreate = draft.isValid && (!vendorPicker || !!vendorPicker.vendorId);
  const submit = () => (canCreate ? draft.submit() : undefined);
  // Opening an existing item replaces the form, so a typed draft is only dropped once confirmed.
  const hasDraft = !!draft.title.trim() || !!draft.description.trim();
  const openExisting = (item: api.VendorActionItemResponse) => {
    if (hasDraft && !window.confirm('Discard this draft?')) return;
    vendorPicker?.onOpenItem(item);
  };

  return (
    <Form>
      {vendorPicker ? (
        // A div, not a label: react-select's input isn't the label's first control.
        <Label as="div">
          Vendor
          <PickerControl>
            <VendorSelect
              selectedVendorId={vendorPicker.vendorId}
              onSelectedVendorChange={(id) => vendorPicker.onChange(id ?? undefined)}
              quickOptions={vendorPicker.quickOptions}
            />
          </PickerControl>
        </Label>
      ) : (
        <Label>
          Vendor
          <FixedValue>{vendorName}</FixedValue>
        </Label>
      )}
      {sourceNote && <SourceNoteQuote note={sourceNote} maxHeight={140} />}
      <Label>
        Title
        <TextInput
          autoFocus
          value={draft.title}
          placeholder="What needs to happen?"
          onChange={(e) => draft.setTitle(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
        />
      </Label>
      <Label>
        Description
        <TextArea value={draft.description} placeholder="Add a description…" onChange={(e) => draft.setDescription(e.target.value)} />
      </Label>
      <Label>
        Assignee
        <Select value={draft.assignedTo} onChange={(e) => draft.setAssignedTo(e.target.value)}>
          <AssigneeOptions operators={operators} me={me} />
        </Select>
      </Label>
      {error && <ErrorText role="alert">{error}</ErrorText>}
      <Actions>
        <Button text="Cancel" color="secondary" onClick={onCancel} />
        <Button text="Create" disabled={!canCreate} isLoading={draft.saving} onClick={submit} />
      </Actions>
      {vendorPicker?.vendorId && (
        <VendorActionItemsSection
          vendorId={vendorPicker.vendorId}
          label="Vendor Action Items"
          me={me}
          canWrite={false}
          onOpen={openExisting}
        />
      )}
    </Form>
  );
}
